import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Combobox, MultiCombobox } from "@/components/TechCombobox";
import { TECH_CAPABILITIES, TECH_VALIDATION, TECH_VALIDATION_STYLES } from "@/lib/reconConstants";
import { ServerCog, Save, Cloud, Building2, LayoutTemplate, Copy, ChevronRight, AlertTriangle } from "lucide-react";

const Badge = ({ className, children }) => (<span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>);

export default function CustomerTechProfile() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [profile, setProfile] = useState({ capabilities: {} });
  const [editCap, setEditCap] = useState(null);
  const [me, setMe] = useState(null);
  const [catalog, setCatalog] = useState({ product: [], vendor: [], environment: [], authorization: [], evidence_source: [], system_category: [], contractual_approvals: [] });
  const [templates, setTemplates] = useState({});
  const [tplOpen, setTplOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const loadCatalog = useCallback(() => {
    api.get("/blueprints/tech-catalog").then((r) => {
      const g = { product: [], vendor: [], environment: [], authorization: [], evidence_source: [], system_category: [] };
      (r.data.items || []).forEach((it) => { if (g[it.kind]) g[it.kind].push(it); });
      setCatalog({ ...g, contractual_approvals: r.data.contractual_approvals || [] });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/recon/reconciliations").then((r) => {
      const map = {};
      r.data.forEach((x) => { map[x.customer_id] = x.customer_name; });
      setCustomers(Object.entries(map).map(([id, name]) => ({ id, name })));
    }).catch(() => {});
    api.get("/recon/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/blueprints/tech-templates").then((r) => setTemplates(r.data.templates || {})).catch(() => {});
    loadCatalog();
  }, [loadCatalog]);

  const load = useCallback(() => {
    if (!customerId) return;
    api.get(`/blueprints/tech-profile/${customerId}`).then((r) => setProfile(r.data)).catch(() => setProfile({ capabilities: {} }));
  }, [customerId]);
  useEffect(() => { load(); }, [load]);

  const caps = profile.capabilities || {};
  const canAddCatalog = !!me?.caps?.create; // super_admin + compliance_admin
  const customerName = customers.find((c) => c.id === customerId)?.name || "";

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Operations · Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <ServerCog className="h-6 w-6 text-gold" aria-hidden="true" /> Customer Technology Profile
        </h1>
        <p className="text-slate-300 text-sm mt-2 max-w-3xl">Record the customer's selected system for each security capability. This drives Microsoft-first-with-customer-tool-compatibility evidence requests. A system is not approved for CUI simply because it appears in the Microsoft catalog. Manage the reusable catalog under Admin Settings → Technology Catalog.</p>
      </header>

      <div className="p-6 md:p-8 space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="max-w-md space-y-1.5 flex-1 min-w-[240px]">
            <Label className="text-xs">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="tech-customer"><SelectValue placeholder="Select a customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {customerId && me?.caps?.create && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setTplOpen(true)} data-testid="tech-apply-template-btn">
                <LayoutTemplate className="h-4 w-4 mr-1.5" /> Apply Template
              </Button>
              <Button variant="outline" onClick={() => setCopyOpen(true)} data-testid="tech-copy-btn">
                <Copy className="h-4 w-4 mr-1.5" /> Copy from Customer
              </Button>
            </div>
          )}
        </div>

        {customerId && (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-border text-left">{["Capability", "Selected system", "Type", "Environment", "CUI", "Validation", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody>
                {TECH_CAPABILITIES.map((cap) => {
                  const c = caps[cap] || {};
                  return (
                    <tr key={cap} className="border-b border-border last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => setEditCap({ capability: cap, ...c })} data-testid={`tech-row-${cap.replace(/\s+/g, '-')}`}>
                      <td className="px-3 py-3 font-medium text-navy">{cap}</td>
                      <td className="px-3 py-3">
                        {c.selected_product || <span className="text-muted-foreground">— not set —</span>}
                        {c.pending_catalog_review && <Badge className="ml-2 bg-amber-50 text-amber-800 border-amber-200">Pending Catalog Review</Badge>}
                      </td>
                      <td className="px-3 py-3 text-xs">{c.is_microsoft ? <Badge className="bg-blue-50 text-blue-800 border-blue-200"><Cloud className="h-3 w-3 mr-1" />Microsoft</Badge> : (c.vendor || "—")}</td>
                      <td className="px-3 py-3 text-xs">{c.environment || "—"}</td>
                      <td className="px-3 py-3 text-xs">{c.cui_permitted ? "Yes" : "—"}</td>
                      <td className="px-3 py-3">{c.validation_status ? <Badge className={TECH_VALIDATION_STYLES[c.validation_status]}>{c.validation_status}</Badge> : <Badge className={TECH_VALIDATION_STYLES["Not Reviewed"]}>Not Reviewed</Badge>}</td>
                      <td className="px-3 py-3 text-right text-xs text-gold-dark">Edit</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        )}
      </div>

      <CapabilityEditor cap={editCap} customerId={customerId} customerName={customerName} catalog={catalog}
        canAddCatalog={canAddCatalog} onCatalogChanged={loadCatalog}
        onClose={() => setEditCap(null)} onSaved={() => { load(); }}
        onNext={(nextCap) => setEditCap({ capability: nextCap, ...(caps[nextCap] || {}) })} />

      <ApplyTemplateDialog open={tplOpen} onOpenChange={setTplOpen} templates={templates} profile={profile}
        customerId={customerId} onApplied={() => { setTplOpen(false); load(); }} />

      <CopyProfileDialog open={copyOpen} onOpenChange={setCopyOpen} customers={customers} customerId={customerId}
        onCopied={() => { setCopyOpen(false); load(); }} />
    </div>
  );
}

function CapabilityEditor({ cap, customerId, customerName, catalog, canAddCatalog, onCatalogChanged, onClose, onSaved, onNext }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(cap ? { validation_status: "Not Reviewed", evidence_sources: [], ...cap } : {}); }, [cap]);
  if (!cap) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const productOpts = (catalog.product || []).map((p) => ({
    value: p.value, description: [p.vendor, p.is_microsoft ? "Microsoft-native" : null].filter(Boolean).join(" · "),
  }));
  const capMatched = (catalog.product || []).filter((p) => (p.capabilities || []).includes(cap.capability));
  const orderedProducts = [
    ...capMatched.map((p) => ({ value: p.value, description: `Recommended · ${p.vendor}${p.is_microsoft ? " · Microsoft" : ""}` })),
    ...productOpts.filter((o) => !capMatched.some((m) => m.value === o.value)),
  ];

  const onSelectProduct = (val) => {
    const meta = (catalog.product || []).find((p) => p.value.toLowerCase() === val.toLowerCase());
    if (meta) {
      setF((s) => ({
        ...s, selected_product: val, vendor: meta.vendor || s.vendor || "", is_microsoft: !!meta.is_microsoft,
        system_category: meta.system_category || s.system_category || "",
        evidence_sources: (meta.default_evidence_sources && meta.default_evidence_sources.length) ? meta.default_evidence_sources : (s.evidence_sources || []),
        pending_catalog_review: false,
      }));
    } else {
      set("selected_product", val);
    }
  };

  // When a value not in catalog is entered, persist or propose based on role.
  const handleAddCatalog = async (kind, value, extra = {}) => {
    if (canAddCatalog) {
      try {
        await api.post("/blueprints/tech-catalog", { kind, value, ...extra });
        onCatalogChanged();
        toast.success(`Added "${value}" to the catalog.`);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not add to catalog."); }
    } else {
      try {
        await api.post("/blueprints/tech-catalog/propose", { kind, value, capability: cap.capability, customer_id: customerId, customer_name: customerName });
        set("pending_catalog_review", true);
        toast.info(`"${value}" saved as customer-specific — flagged Pending Catalog Review.`);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not save value."); }
    }
  };
  const customLabel = canAddCatalog ? "Add to catalog" : "Use as customer-specific";

  const doSave = async () => {
    await api.put(`/blueprints/tech-profile/${customerId}`, {
      capability: cap.capability, selected_product: f.selected_product || "", vendor: f.vendor || "",
      is_microsoft: !!f.is_microsoft, environment: f.environment || "", cui_permitted: !!f.cui_permitted,
      contractual_approval: f.contractual_approval || "", authorization_info: f.authorization_info || "",
      data_stored: !!f.data_stored, data_transmitted: !!f.data_transmitted, data_processed: !!f.data_processed,
      system_owner: f.system_owner || "", implementation_partner: f.implementation_partner || "",
      evidence_source: (f.evidence_sources || []).join(", "), evidence_sources: f.evidence_sources || [],
      system_category: f.system_category || "", notes: f.notes || "",
      validation_status: f.validation_status || "Not Reviewed", pending_catalog_review: !!f.pending_catalog_review,
    });
  };
  const save = async () => {
    try { await doSave(); toast.success("Capability saved."); onSaved(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const saveNext = async () => {
    try {
      await doSave(); toast.success("Saved."); onSaved();
      const idx = TECH_CAPABILITIES.indexOf(cap.capability);
      const next = TECH_CAPABILITIES[(idx + 1) % TECH_CAPABILITIES.length];
      onNext(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };

  return (
    <Sheet open={!!cap} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="tech-editor-sheet">
        <SheetHeader><SheetTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-gold" /> {cap.capability}</SheetTitle>
          <SheetDescription>Select the customer's system from the catalog, or add a custom value. Selecting a known product auto-fills vendor, Microsoft status, category and suggested evidence sources.</SheetDescription></SheetHeader>
        <div className="mt-4 space-y-3 text-sm">
          <div className="space-y-1.5"><Label className="text-xs">Selected product or process</Label>
            <Combobox value={f.selected_product || ""} onChange={onSelectProduct} options={orderedProducts}
              placeholder="Search products…" allowCustom customLabel={customLabel} testid="tech-product"
              onAddCustom={(v) => handleAddCatalog("product", v, { capabilities: [cap.capability], vendor: f.vendor || "", system_category: f.system_category || "", is_microsoft: !!f.is_microsoft })} />
            {f.pending_catalog_review && (
              <p className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Flagged Pending Catalog Review — an admin can promote this into the shared catalog.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Vendor</Label>
              <Combobox value={f.vendor || ""} onChange={(v) => set("vendor", v)} options={(catalog.vendor || []).map((x) => x.value)}
                placeholder="Vendor…" allowCustom customLabel={customLabel} testid="tech-vendor"
                onAddCustom={(v) => handleAddCatalog("vendor", v)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Environment</Label>
              <Combobox value={f.environment || ""} onChange={(v) => set("environment", v)} options={(catalog.environment || []).map((x) => x.value)}
                placeholder="Environment…" allowCustom customLabel={customLabel} testid="tech-env"
                onAddCustom={(v) => handleAddCatalog("environment", v)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">System category</Label>
            <Combobox value={f.system_category || ""} onChange={(v) => set("system_category", v)} options={(catalog.system_category || []).map((x) => x.value)}
              placeholder="Category…" allowCustom customLabel={customLabel} testid="tech-category"
              onAddCustom={(v) => handleAddCatalog("system_category", v)} /></div>
          <div className="flex flex-wrap gap-4 text-xs">
            <label className="flex items-center gap-1.5"><Checkbox checked={!!f.is_microsoft} onCheckedChange={(v) => set("is_microsoft", !!v)} data-testid="tech-ms" /> Microsoft-native</label>
            <label className="flex items-center gap-1.5"><Checkbox checked={!!f.cui_permitted} onCheckedChange={(v) => set("cui_permitted", !!v)} data-testid="tech-cui" /> CUI permitted</label>
          </div>
          <div className="flex flex-wrap gap-4 text-xs">
            <label className="flex items-center gap-1.5"><Checkbox checked={!!f.data_stored} onCheckedChange={(v) => set("data_stored", !!v)} /> Data stored</label>
            <label className="flex items-center gap-1.5"><Checkbox checked={!!f.data_transmitted} onCheckedChange={(v) => set("data_transmitted", !!v)} /> Data transmitted</label>
            <label className="flex items-center gap-1.5"><Checkbox checked={!!f.data_processed} onCheckedChange={(v) => set("data_processed", !!v)} /> Data processed</label>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Contractual approval status</Label>
            <Select value={f.contractual_approval || ""} onValueChange={(v) => set("contractual_approval", v)}>
              <SelectTrigger data-testid="tech-approval"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{(catalog.contractual_approvals || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs">FedRAMP / authorization</Label>
            <Combobox value={f.authorization_info || ""} onChange={(v) => set("authorization_info", v)} options={(catalog.authorization || []).map((x) => x.value)}
              placeholder="Authorization…" allowCustom customLabel={customLabel} testid="tech-auth"
              onAddCustom={(v) => handleAddCatalog("authorization", v)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">System owner</Label><Input value={f.system_owner || ""} onChange={(e) => set("system_owner", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Implementation partner</Label><Input value={f.implementation_partner || ""} onChange={(e) => set("implementation_partner", e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Evidence sources</Label>
            <MultiCombobox value={f.evidence_sources || []} onChange={(v) => set("evidence_sources", v)} options={(catalog.evidence_source || []).map((x) => x.value)}
              placeholder="Select evidence sources…" allowCustom customLabel={customLabel} testid="tech-evidence"
              onAddCustom={(v) => handleAddCatalog("evidence_source", v)} />
            {(f.evidence_sources || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">{(f.evidence_sources || []).map((s) => <Badge key={s} className="bg-slate-100 text-slate-700 border-slate-200">{s}</Badge>)}</div>
            )}
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Validation status</Label>
            <Select value={f.validation_status} onValueChange={(v) => set("validation_status", v)}><SelectTrigger data-testid="tech-validation"><SelectValue /></SelectTrigger>
              <SelectContent>{TECH_VALIDATION.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          <div className="flex gap-2 pt-1">
            <Button className="flex-1 bg-gold text-navy hover:bg-gold-dark" onClick={save} data-testid="tech-save"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
            <Button variant="outline" className="flex-1" onClick={saveNext} data-testid="tech-save-next">Save &amp; Add Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ApplyTemplateDialog({ open, onOpenChange, templates, profile, customerId, onApplied }) {
  const [tpl, setTpl] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setTpl(""); setOverwrite(false); } }, [open]);
  const caps = profile.capabilities || {};
  const entries = tpl && templates[tpl] ? Object.entries(templates[tpl]) : [];
  const willSet = entries.filter(([cap]) => overwrite || !caps[cap]?.selected_product);
  const willSkip = entries.filter(([cap]) => !overwrite && caps[cap]?.selected_product);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/blueprints/tech-profile/${customerId}/apply-template`, { template: tpl, overwrite });
      toast.success(`Template applied — ${r.data.applied} capabilities set to "Not Reviewed".`);
      onApplied();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="tech-template-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-gold" /> Apply Technology Profile Template</DialogTitle>
          <DialogDescription>Pre-fills selected systems for common baselines. Every capability is set to "Not Reviewed" — nothing is auto-approved for CUI, and no authorization is confirmed.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5"><Label className="text-xs">Template</Label>
            <Select value={tpl} onValueChange={setTpl}>
              <SelectTrigger data-testid="tech-template-select"><SelectValue placeholder="Choose a baseline template" /></SelectTrigger>
              <SelectContent>{Object.keys(templates).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="tech-template-overwrite" /> Overwrite capabilities that already have a selected system</label>
          {tpl && (
            <div className="rounded border border-border max-h-64 overflow-y-auto" data-testid="tech-template-preview">
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50 border-b text-left"><th className="px-3 py-2 font-semibold text-navy">Capability</th><th className="px-3 py-2 font-semibold text-navy">Will set to</th><th className="px-3 py-2 font-semibold text-navy">Action</th></tr></thead>
                <tbody>
                  {entries.map(([cap, prod]) => {
                    const skip = willSkip.some(([c]) => c === cap);
                    return (<tr key={cap} className="border-b last:border-0">
                      <td className="px-3 py-1.5 font-medium text-navy">{cap}</td>
                      <td className="px-3 py-1.5">{prod}</td>
                      <td className="px-3 py-1.5">{skip ? <span className="text-muted-foreground">Skip (already set)</span> : <span className="text-emerald-700">Set</span>}</td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          )}
          {tpl && <p className="text-[11px] text-muted-foreground">{willSet.length} will be set · {willSkip.length} skipped. Existing entries are preserved unless overwrite is checked.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-gold text-navy hover:bg-gold-dark" disabled={!tpl || busy || willSet.length === 0} onClick={apply} data-testid="tech-template-apply">Apply Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyProfileDialog({ open, onOpenChange, customers, customerId, onCopied }) {
  const [src, setSrc] = useState("");
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setSrc(""); setPreview([]); } }, [open]);
  useEffect(() => {
    if (!src || !customerId) { setPreview([]); return; }
    api.get(`/blueprints/tech-profile/${customerId}/copy-preview`, { params: { source_customer_id: src } })
      .then((r) => setPreview(r.data.capabilities || [])).catch(() => setPreview([]));
  }, [src, customerId]);

  const copy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/blueprints/tech-profile/${customerId}/copy-from`, { source_customer_id: src });
      toast.success(`Copied ${r.data.copied} capabilities. Validation reset to "Not Reviewed".`);
      onCopied();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to copy profile."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="tech-copy-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5 text-gold" /> Copy Technology Profile</DialogTitle>
          <DialogDescription>Copies selected systems from another customer. CUI approval, validation status, owners, approvals and notes are reset — you must re-validate for this customer.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5"><Label className="text-xs">Copy from customer</Label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger data-testid="tech-copy-source"><SelectValue placeholder="Select source customer" /></SelectTrigger>
              <SelectContent>{customers.filter((c) => c.id !== customerId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          {src && (
            <div className="rounded border border-border max-h-64 overflow-y-auto" data-testid="tech-copy-preview">
              {preview.length === 0 ? <p className="px-3 py-4 text-xs text-muted-foreground">This customer has no selected systems to copy.</p> : (
                <table className="w-full text-xs"><tbody>
                  {preview.map((p) => <tr key={p.capability} className="border-b last:border-0"><td className="px-3 py-1.5 font-medium text-navy">{p.capability}</td><td className="px-3 py-1.5">{p.selected_product}</td></tr>)}
                </tbody></table>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-gold text-navy hover:bg-gold-dark" disabled={!src || busy || preview.length === 0} onClick={copy} data-testid="tech-copy-apply">Copy Profile</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
